import { useCallback, useRef, useState } from "react";
import client from "libs/client";
import { _ } from "i18n";
import { APIResponse } from "types";
import { useToast } from "ui-kit";
import { isGranted } from "pages/productManagement/functions";
import { Permission, Resource } from "pages/productManagement/types";
import { ProgramGameClubTeamSide } from "../constants";
import type { ProgramGameFormState, ProgramGameSelectOption } from "../types";
import { buildCreatedClubOption } from "../createdClubOption";

interface UseProgramGameClubCreationOptions {
	updateForm: (
		patch: Partial<ProgramGameFormState> | ((prev: ProgramGameFormState) => ProgramGameFormState),
	) => void;
}

export function useProgramGameClubCreation({ updateForm }: UseProgramGameClubCreationOptions) {
	const canCreateClub = isGranted(Resource.NON_PARTNER_CLUB, Permission.CREATE);
	const [activeSide, setActiveSide] = useState<ProgramGameClubTeamSide | null>(null);
	const [prefillName, setPrefillName] = useState("");
	const isSubmittedRef = useRef(false);
	const { addSuccessToast, addErrorToast } = useToast();

	const requestCreate = useCallback((side: ProgramGameClubTeamSide, name: string) => {
		setPrefillName(name);
		setActiveSide(side);
	}, []);

	const close = useCallback(() => {
		setActiveSide(null);
		setPrefillName("");
	}, []);

	const selectClub = useCallback(
		(side: ProgramGameClubTeamSide, option: ProgramGameSelectOption) => {
			updateForm((prev) =>
				side === ProgramGameClubTeamSide.Home
					? { ...prev, homeClub: option, homeTeam: null }
					: { ...prev, awayClub: option, awayTeam: null },
			);
		},
		[updateForm],
	);

	// Matches ClubModal's `onUpdateClub: (values: any) => Promise<void>` prop.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onUpdateClub = useCallback(
		async (values: any) => {
			if (activeSide === null) {
				return;
			}
			isSubmittedRef.current = true;
			try {
				const res = await client.addUpdateNPClub.mutation({ body: { nonPartnerClub: values } });
				if (res?.data.status === APIResponse.SUCCESS) {
					const option = buildCreatedClubOption(res.data.data);
					if (option) {
						selectClub(activeSide, option);
					}
					if (res.data.message) {
						addSuccessToast(res.data.message);
					}
					close();
				} else if (res?.data.message) {
					addErrorToast(res.data.message);
				}
			} catch {
				addErrorToast(_("message.error.generic"));
			} finally {
				isSubmittedRef.current = false;
			}
		},
		[activeSide, addErrorToast, addSuccessToast, close, selectClub],
	);

	return { canCreateClub, activeSide, prefillName, requestCreate, close, onUpdateClub, isSubmittedRef };
}
